import { Button, Form, Input, Modal, InputNumber, DatePicker, Select, Switch } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { nullSafeGetOrElse, nullSafeGet } from '../../utils/DataAccessUtils';
import GeneralStatusDisplay from '../AdminCycleEventsIndexPage/GeneralStatusDisplay';
import { ACC_AUDIT_KEY_DISPLAY_NAMES } from '../../utils/AssetCycleUtils';
import { ASSET_FIELDS_LABELS_MAP, ASSET_FIELDS_DETAILS } from '../../utils/assetUtils';
import OWUpload from '../OWUpload';
import { imageUploadValidation, normFile } from '../../utils/ImageUtils';
import { getProtectedImageUriGeneric } from '../../utils/FileAccessUtils';
import { PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import { getCurrency } from '../../utils/DataAccessUtils';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import {
	assetModelsRestCrudThunksForBuyer,
	assetModelsRestCrudThunksForSupplier,
} from '../../thunks/asset_models_thunks';
import { withUserType } from '../withUserType';
import { ROLE_TYPES } from '../../utils/DataConstants';

const FORM_NAME = 'asset-audit-form';
const dateFormat = 'MMMM DD, YYYY';

function AssetAuditForm({
	assetAudit,
	cycleRun,
	onCancel,
	onSubmit,
	assetModels,
	fetchAssetModels,
	fetchMultipleAssetModels,
	currentUser,
}) {
	const [form] = Form.useForm();
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewImage, setPreviewImage] = useState('');
	const [updating, setUpdating] = useState(false);

	const capturedFields = useMemo(
		() => nullSafeGetOrElse('capturedFields', assetAudit, []),
		[assetAudit]
	);

	const captureFields = useMemo(
		() => nullSafeGetOrElse('assetCycleCountEvent.captureFields', cycleRun, []),
		[cycleRun]
	);

	const qrCodeImagesField = useMemo(
		() =>
			captureFields.find((field) => {
				const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === field.key);
				return fieldDetails && fieldDetails.key === 'qrCodeImages';
			}),
		[captureFields]
	);

	const serialNumberImagesField = useMemo(
		() =>
			captureFields.find((field) => {
				const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === field.key);
				return fieldDetails && fieldDetails.key === 'serialNumberImages';
			}),
		[captureFields]
	);

	const hasQrCodeImages = !!qrCodeImagesField;
	const hasSerialNumberImages = !!serialNumberImagesField;

	const handlePreview = useCallback((file) => {
		setPreviewImage(file.url || file.thumbUrl);
		setPreviewVisible(true);
	}, []);

	const handleCancel = useCallback(() => {
		setPreviewVisible(false);
	}, []);

	// Get assetTypeId from assetAudit for filtering asset models
	const assetTypeId = useMemo(() => {
		return (
			nullSafeGet('asset.assetTypeId', assetAudit) || nullSafeGet('asset.assetType.id', assetAudit)
		);
	}, [assetAudit]);

	// Get currency from currentUser
	const currency = useMemo(() => {
		return getCurrency({ currentUser });
	}, [currentUser]);

	// Render appropriate input component based on field type from ASSET_FIELDS_DETAILS
	const renderFieldInput = useCallback(
		(field) => {
			const fieldKey = field.key;
			// Get field type from ASSET_FIELDS_DETAILS or from field definition
			const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === fieldKey);
			const fieldType = nullSafeGetOrElse('type', fieldDetails, 'text');

			// Handle assetModelId with full async select functionality (same as AssetForm)
			// Check if field exists in ASSET_FIELDS_DETAILS and is assetModelId
			if (fieldDetails && fieldDetails.key === 'assetModelId') {
				return (
					<OWAsyncSelect
						targetCollectionName="ASSET_MODELS_AUTOCOMPLETE"
						stateSlice={assetModels}
						fetchMultiple={(ids, targetCollectionName) => {
							fetchMultipleAssetModels(ids, targetCollectionName);
						}}
						fetchData={(searchText, targetCollectionName) => {
							fetchAssetModels(
								{
									modelName: searchText,
									assetTypeId: assetTypeId,
								},
								targetCollectionName
							);
						}}
						placeholder={'Select Model'}
						renderRecord={(model) => (
							<Select.Option key={model.id} value={model.id}>
								{model.modelName}
							</Select.Option>
						)}
						additionalFilters={{
							assetTypeId: assetTypeId,
						}}
						sortBy={{ sort_by: 'name', order: 'ascend' }}
					/>
				);
			}

			switch (fieldType) {
				case 'date':
					return <DatePicker format={dateFormat} style={{ width: '100%' }} />;
				case 'currency':
					return (
						<InputNumber
							addonAfter={currency && currency.id ? currency.id : 'USD'}
							style={{ width: '100%' }}
						/>
					);
				case 'switch':
					return <Switch />;
				case 'text':
				case 'textScanner':
				default:
					return <Input />;
			}
		},
		[assetModels, fetchAssetModels, fetchMultipleAssetModels, assetTypeId, currency]
	);

	// Convert [{fileName, fileId}] to fileList format for Ant Design Upload
	const toFileList = (images) => {
		if (!images || !Array.isArray(images)) return [];
		return images
			.filter((img) => img.fileId && img.fileName)
			.map((img, idx) => ({
				uid: `-${idx}-${img.fileId}`,
				name: img.fileName,
				status: 'done',
				url: getProtectedImageUriGeneric(img.fileId, img.fileName),
				thumbUrl: getProtectedImageUriGeneric(img.fileId, img.fileName),
				response: { data: { fileName: img.fileName, fileId: img.fileId } },
			}));
	};

	// Convert fileList format back to [{fileName, fileId}]
	const fromFileList = (fileList) => {
		if (!fileList || !Array.isArray(fileList)) return [];
		return fileList
			.map((file) => {
				const data = file.response && file.response.data ? file.response.data : null;
				return data && data.fileName && data.fileId
					? { fileName: data.fileName, fileId: data.fileId }
					: null;
			})
			.filter(Boolean);
	};

	const handleSubmit = useCallback(
		(values) => {
			if (onSubmit) {
				const updatedCapturedFields = captureFields
					.filter((field) => {
						const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === field.key);
						const fieldType = nullSafeGetOrElse('type', fieldDetails, null);
						return fieldType !== 'images';
					})
					.map((field) => {
						const fieldValue = values[field.key];
						const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === field.key);
						const fieldType = nullSafeGetOrElse('type', fieldDetails, 'text');

						// Process value matching AssetForm - only convert specific fields
						let processedValue = fieldValue;

						// Convert assetModelId to integer (matching AssetForm line 307)
						if (fieldDetails && fieldDetails.key === 'assetModelId' && processedValue) {
							processedValue = parseInt(processedValue, 10);
						}

						// Convert currency fields to float (matching AssetForm lines 314-315)
						if (fieldType === 'currency' && processedValue) {
							processedValue = parseFloat(processedValue);
						}

						return {
							key: field.key,
							value: processedValue === '' || processedValue === null ? undefined : processedValue,
						};
					});

				const patchData: any = {
					capturedFields: updatedCapturedFields,
				};

				if (values.notes !== undefined) {
					patchData.notes = values.notes;
				}
				if (values.qrCodeImages) {
					patchData.qrCodeImages = fromFileList(values.qrCodeImages);
				}
				if (values.serialNumberImages) {
					patchData.serialNumberImages = fromFileList(values.serialNumberImages);
				}
				setUpdating(true);
				onSubmit(patchData);
			}
		},
		[onSubmit, captureFields]
	);

	const initialValues = useMemo(() => {
		const capturedFieldsMap = capturedFields.reduce((acc, field) => {
			acc[field.key] = field.value;
			return acc;
		}, {});

		const values = captureFields.reduce((acc, field) => {
			const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === field.key);
			const fieldType = nullSafeGetOrElse('type', fieldDetails, 'text');

			if (fieldType === 'images') {
				const images = nullSafeGetOrElse(field.key, assetAudit, []);
				acc[field.key] = toFileList(images);
			} else {
				const fieldValue = capturedFieldsMap[field.key];
				const fieldKey = field.key;
				// Get field type from ASSET_FIELDS_DETAILS or from field definition
				const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === fieldKey);
				const fieldType =
					field.type || field.valueType || nullSafeGetOrElse('type', fieldDetails, 'text');

				// Convert date strings to moment objects for DatePicker
				if (fieldType === 'date' && fieldValue) {
					acc[field.key] = moment(fieldValue);
				} else {
					acc[field.key] = fieldValue !== undefined ? fieldValue : undefined;
				}
			}
			return acc;
		}, {});

		return {
			...values,
			notes: nullSafeGetOrElse('notes', assetAudit, ''),
			qrCodeImages: toFileList(nullSafeGetOrElse('qrCodeImages', assetAudit, [])),
			serialNumberImages: toFileList(nullSafeGetOrElse('serialNumberImages', assetAudit, [])),
		};
	}, [captureFields, capturedFields, assetAudit]);

	return (
		<Modal
			title={
				<div className=" flex flex-col gap-2">
					<div className="h4medium">
						{nullSafeGetOrElse('asset.name', assetAudit, '')},{' '}
						{nullSafeGetOrElse('asset.assetType.name', assetAudit, '')}
					</div>
					<GeneralStatusDisplay
						statusKey={assetAudit.matchStatus}
						statusLabel={nullSafeGetOrElse(
							assetAudit.matchStatus,
							ACC_AUDIT_KEY_DISPLAY_NAMES,
							assetAudit.matchStatus
						)}
					/>
				</div>
			}
			visible
			onCancel={onCancel}
			footer={[
				<Button key="cancel" onClick={onCancel} size="large">
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					size="large"
					style={{ marginLeft: '16px' }}
					htmlType="submit"
					form={FORM_NAME}
					loading={updating}
				>
					Save Changes
				</Button>,
			]}
		>
			<Form
				key={assetAudit && assetAudit.id ? assetAudit.id : 'form'}
				id={FORM_NAME}
				form={form}
				layout="vertical"
				onFinish={handleSubmit}
				initialValues={initialValues}
			>
				{captureFields
					.filter((field) => {
						const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === field.key);
						const fieldType = nullSafeGetOrElse('type', fieldDetails, 'text');
						return fieldType !== 'images';
					})
					.map((field) => {
						const fieldKey = field.key;
						const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === fieldKey);
						const fieldType =
							field.type || field.valueType || nullSafeGetOrElse('type', fieldDetails, 'text');
						const isBoolean = fieldType === 'switch';

						return (
							<Form.Item
								key={field.key}
								label={ASSET_FIELDS_LABELS_MAP[field.key]}
								name={field.key}
								valuePropName={isBoolean ? 'checked' : 'value'}
								rules={
									field.isRequired
										? [
												{
													required: true,
													message: `${ASSET_FIELDS_LABELS_MAP[field.key] || field.key} is required`,
												},
										  ]
										: undefined
								}
							>
								{renderFieldInput(field)}
							</Form.Item>
						);
					})}
				<Form.Item name="notes" label="Additional Notes">
					<Input.TextArea />
				</Form.Item>
				{hasQrCodeImages ? (
					<Form.Item
						name="qrCodeImages"
						label={ASSET_FIELDS_LABELS_MAP['qrCodeImages']}
						valuePropName="fileList"
						getValueFromEvent={normFile}
						rules={
							qrCodeImagesField && qrCodeImagesField.isRequired
								? [
										{
											required: true,
											message: `${ASSET_FIELDS_LABELS_MAP['qrCodeImages']} is required`,
										},
								  ]
								: undefined
						}
					>
						<OWUpload
							accept="image/*"
							name="file"
							listType="picture-card"
							className="avatar-uploader"
							beforeUpload={imageUploadValidation}
							onPreview={handlePreview}
							maxCount={1}
						>
							<div style={{ display: 'inline' }}>
								{<PlusOutlined />}
								<div className="ant-upload-text">Upload</div>
							</div>
						</OWUpload>
					</Form.Item>
				) : null}
				{hasSerialNumberImages ? (
					<Form.Item
						name="serialNumberImages"
						label={ASSET_FIELDS_LABELS_MAP['serialNumberImages']}
						valuePropName="fileList"
						getValueFromEvent={normFile}
						rules={
							serialNumberImagesField && serialNumberImagesField.isRequired
								? [
										{
											required: true,
											message: `${ASSET_FIELDS_LABELS_MAP['serialNumberImages']} is required`,
										},
								  ]
								: undefined
						}
					>
						<OWUpload
							accept="image/*"
							name="file"
							listType="picture-card"
							className="avatar-uploader"
							beforeUpload={imageUploadValidation}
							onPreview={handlePreview}
							maxCount={1}
						>
							<div style={{ display: 'inline' }}>
								{<PlusOutlined />}
								<div className="ant-upload-text">Upload</div>
							</div>
						</OWUpload>
					</Form.Item>
				) : null}
				<Modal visible={previewVisible} footer={null} onCancel={handleCancel}>
					<img alt="preview" style={{ width: '100%' }} src={previewImage} />
				</Modal>
			</Form>
		</Modal>
	);
}

const mapStateToProps = (state) => ({
	assetModels: state.asset_models,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchAssetModels: (params, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetModelsRestCrudThunksForSupplier.readLite(
						{ ...params, no_pagination: true },
						targetCollectionName
				  )
				: assetModelsRestCrudThunksForBuyer.readLite(
						{ ...params, no_pagination: true },
						targetCollectionName
				  )
		),
	fetchMultipleAssetModels: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetModelsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: assetModelsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(AssetAuditForm));
